import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  Wand2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import {
  SKILL_FOCUS_OPTIONS,
  GRADE_OPTIONS,
  SUBJECT_OPTIONS,
} from "../lib/options";
import { analyseResourceFile, analyseResourceText } from "../lib/api";
import {
  getGradesFromCurriculum,
  getSpecificsForGradeSubject,
  getStrandsForGradeSubject,
  getSubjectsForGrade,
  useCurriculum,
} from "../lib/curriculum";

const MAX_UPLOAD_MB = 12;
const MAX_PASTE_CHARS = 6000;
const TEXT_TOO_LONG_MSG =
  "Text is too long. Paste the specific section you'd like to use — roughly 5 pages maximum.";
const ACCEPTED_EXTS = [
  "pdf",
  "docx",
  "pptx",
  "txt",
  "md",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "bmp",
  "tif",
  "tiff",
];
const ACCEPT_ATTR = ACCEPTED_EXTS.map((e) => `.${e}`).join(",")
  + ",application/pdf,image/*,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain";

const FRIENDLY_TYPES = "PDF · Word (DOCX) · PowerPoint (PPTX) · Image (PNG/JPG) · Text";
const OUTPUT_OPTIONS = [
  { value: "lesson", label: "Full lesson", hint: "A complete classroom-ready lesson" },
  { value: "unit", label: "Unit plan", hint: "Several linked lessons with per-day prompts" },
  { value: "activity", label: "Activity only", hint: "A focused 10–25 min activity" },
];
const LESSON_COUNT_OPTIONS = ["3", "5", "7", "10", "12"];

const fieldClass =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-12";

const PrivacyLine = ({ children }) => (
  <p className="mt-3 text-xs text-[#4a6984]/65 flex items-start gap-1.5 leading-relaxed">
    <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#1c2d3d]/70" />
    <span>{children}</span>
  </p>
);

const StepBadge = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <span
      className={`h-7 w-7 rounded-full grid place-items-center text-xs font-black ${
        active
          ? "bg-[#1c2d3d] text-white"
          : done
            ? "bg-[#96ded1] text-[#1c2d3d]"
            : "bg-[#4a6984]/15 text-[#4a6984]/60"
      }`}
    >
      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
    </span>
    <span
      className={`font-bold uppercase text-[10px] tracking-[0.18em] ${
        active ? "text-[#1c2d3d]" : done ? "text-[#4a6984]" : "text-[#4a6984]/50"
      }`}
    >
      {label}
    </span>
  </div>
);

export const ResourceBuilder = ({ onGenerate, loading, followupPrefill = null }) => {
  // Step 1 — upload / paste
  const [uploadFile, setUploadFile] = useState(null);
  const [pastedText, setPastedText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const pasteCharCount = pastedText.length;
  const pasteOverLimit = pasteCharCount > MAX_PASTE_CHARS;

  // Step 2 — confirm
  const [analysis, setAnalysis] = useState(null); // server response
  const [confirm, setConfirm] = useState({
    grade: followupPrefill?.grade || "",
    subject: followupPrefill?.subject || "",
    skill_focus: followupPrefill?.skill_focus || "",
    strand: "",
    expectation: "",
    output_type: "lesson",
    num_lessons: "5",
    notes: "",
  });
  const { data: curriculum } = useCurriculum();

  const reset = () => {
    setUploadFile(null);
    setPastedText("");
    setAnalysis(null);
    setFileError("");
    setConfirm({
      grade: "",
      subject: "",
      skill_focus: "",
      strand: "",
      expectation: "",
      output_type: "lesson",
      num_lessons: "5",
      notes: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const subjectOptions = confirm.grade && curriculum
    ? getSubjectsForGrade(curriculum, confirm.grade)
    : SUBJECT_OPTIONS;
  const strandOptions = curriculum
    ? getStrandsForGradeSubject(curriculum, confirm.grade, confirm.subject)
    : [];
  const allSpecifics = curriculum
    ? getSpecificsForGradeSubject(curriculum, confirm.grade, confirm.subject)
    : [];
  const specificOptions = confirm.strand
    ? allSpecifics.filter((row) => row.code && row.code.startsWith(confirm.strand))
    : [];
  const hasCuratedSpecifics = specificOptions.length > 0;
  const gradeOptions = curriculum
    ? getGradesFromCurriculum(curriculum)
    : GRADE_OPTIONS;

  // Cascading resets for the resource confirm step.
  useEffect(() => {
    setConfirm((c) => {
      const validSubjects = curriculum
        ? getSubjectsForGrade(curriculum, c.grade)
        : SUBJECT_OPTIONS;
      const next = { ...c };
      if (c.subject && !validSubjects.includes(c.subject)) {
        next.subject = "";
        next.strand = "";
        next.expectation = "";
      }
      const validStrandCodes = curriculum
        ? getStrandsForGradeSubject(curriculum, next.grade, next.subject).map(
            (s) => s.code
          )
        : [];
      if (next.strand && !validStrandCodes.includes(next.strand)) {
        next.strand = "";
        next.expectation = "";
      }
      return next;
    });
  }, [curriculum, confirm.grade, confirm.subject]);

  const extOf = (name) => {
    const m = /\.([a-z0-9]+)$/i.exec(name || "");
    return (m && m[1].toLowerCase()) || "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = extOf(file.name);
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error("Unsupported file type", {
        description: `Supported: ${FRIENDLY_TYPES}.`,
      });
      return;
    }
    setFileError("");

    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error("File too large", { description: `Max ${MAX_UPLOAD_MB} MB.` });
      return;
    }
    setUploadFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const submitAnalyse = async () => {
    const trimmedText = (pastedText || "").trim();
    if (!uploadFile && !trimmedText) {
      toast.error("Add something to analyse", {
        description: "Drop a file or paste your resource text.",
      });
      return;
    }
    setAnalysing(true);
    try {
      const data = uploadFile
        ? await analyseResourceFile(uploadFile)
        : await analyseResourceText(trimmedText, "Pasted text");
      setAnalysis(data);
      const s = data?.suggested || {};
      // Phase 138.F — when followupPrefill is present (deep-link from
      // a "Build a follow-up" CTA on an earlier lesson), prefer those
      // values over the classifier's suggestions. The classifier
      // sometimes returns shorthand ('Math', 'Critical Thinking') that
      // doesn't match the canonical Select option strings
      // ('Mathematics', 'Critical Thinking and Problem Solving'), which
      // leaves the Selects rendering an empty placeholder. Honouring
      // the URL prefill avoids that mismatch entirely.
      setConfirm((c) => ({
        ...c,
        grade: (followupPrefill?.grade || s.grade || c.grade || ""),
        subject: (followupPrefill?.subject || s.subject || c.subject || ""),
        skill_focus: (followupPrefill?.skill_focus || s.skill_focus || c.skill_focus || ""),
        output_type: s.output_type || c.output_type || "lesson",
      }));
      const methodLabel = {
        "pdf-ocr": "scanned PDF read via OCR",
        "image-ocr": "image read via OCR",
        "pdf-text": "PDF text extracted",
        docx: "Word document parsed",
        pptx: "PowerPoint parsed",
        text: "text parsed",
      }[data?.method] || "resource ready";
      toast.success("Resource ready to confirm", {
        description: `${methodLabel} · tweak the auto-detected fields below if needed.`,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Please try again.";
      toast.error("Analysis failed", { description: String(detail) });
    } finally {
      setAnalysing(false);
    }
  };

  const submitBuild = () => {
    if (!analysis?.extracted_text) return;
    let strandLabel = "";
    if (confirm.strand) {
      const m = strandOptions.find((s) => s.code === confirm.strand);
      strandLabel = m ? `${m.code} — ${m.name}` : confirm.strand;
    }
    onGenerate({
      resource_text: analysis.extracted_text,
      grade: confirm.grade,
      subject: confirm.subject,
      strand: strandLabel,
      expectation: confirm.expectation,
      skill_focus: confirm.skill_focus,
      output_type: confirm.output_type,
      num_lessons:
        confirm.output_type === "unit" ? parseInt(confirm.num_lessons || "5", 10) : undefined,
      prompt: (confirm.notes || "").trim() || undefined,
    });
  };

  const isUnitChoice = confirm.output_type === "unit";

  // ----------------------------------------------------------------- //
  // Smart suggestion — surfaces the classifier's recommended output    //
  // type + grade/subject as a one-tap "Build it now" CTA so teachers   //
  // who agree with Atomic Edu's read of the resource don't have to     //
  // re-click every chip and dropdown manually.                         //
  // ----------------------------------------------------------------- //
  const smartSuggestion = useMemo(() => {
    if (!analysis?.suggested) return null;
    const s = analysis.suggested;
    if (!s.grade || !s.subject || !s.output_type) return null;
    const fmtLabel = {
      lesson: "single lesson",
      unit: "5-day mini-unit",
      activity: "focused activity",
    }[s.output_type] || "lesson";
    const topic = (s.title_guess || "").trim();
    const headline = topic
      ? `${fmtLabel} on ${topic}`
      : `${fmtLabel} for ${s.grade} ${s.subject}`;
    const reasonBits = [s.grade, s.subject, s.skill_focus].filter(Boolean);
    return {
      headline: headline.charAt(0).toUpperCase() + headline.slice(1),
      reason: reasonBits.join(" · "),
      output_type: s.output_type,
      grade: s.grade,
      subject: s.subject,
      skill_focus: s.skill_focus || "",
    };
  }, [analysis]);

  // 1-tap accept — fills the confirm form with the suggestion and
  // submits in the same render cycle so the teacher never sees an
  // intermediate state.
  const buildFromSuggestion = () => {
    if (!smartSuggestion || !analysis?.extracted_text) return;
    onGenerate({
      resource_text: analysis.extracted_text,
      grade: smartSuggestion.grade,
      subject: smartSuggestion.subject,
      strand: "",
      expectation: "",
      skill_focus: smartSuggestion.skill_focus,
      output_type: smartSuggestion.output_type,
      num_lessons: smartSuggestion.output_type === "unit" ? 5 : undefined,
      prompt: undefined,
    });
  };

  return (
    <div
      className="ae-card shadow-xl shadow-[#4a6984]/5 p-6 md:p-10"
      data-testid="resource-builder"
    >
      {/* Progress header */}
      <div className="flex items-center gap-5 flex-wrap mb-7">
        <StepBadge n={1} label="Upload or paste" active={!analysis} done={!!analysis} />
        <span className="text-[#4a6984]/30 font-bold">→</span>
        <StepBadge n={2} label="Confirm details" active={!!analysis} done={false} />
      </div>

      {!analysis ? (
        // ============== STEP 1 ==============
        <>
          {/* Phase 138.F — follow-up banner. Surfaces when the lesson
              workspace deep-linked here with `?followup_from=<id>`. We
              don't store the original source, so we ask the teacher to
              re-upload. The confirm-step grade/subject/skill are
              already pre-seeded from the URL params. */}
          {followupPrefill?.followup_from ? (
            <div
              data-testid="resource-followup-banner"
              className="mb-6 rounded-md border-2 border-[#96ded1] bg-[#96ded1]/15 px-4 py-3"
            >
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#1c2d3d]">
                Building a follow-up
              </p>
              <p className="mt-1 text-sm text-[#4a6984]/90 font-medium">
                Re-upload the same source you used last time. We&apos;ll
                pre-fill {followupPrefill.grade ? <strong>{followupPrefill.grade}</strong> : "your grade"}
                {followupPrefill.subject ? <> · <strong>{followupPrefill.subject}</strong></> : null}
                {followupPrefill.skill_focus ? <> · <strong>{followupPrefill.skill_focus}</strong></> : null}
                {" "}after analysis.
              </p>
            </div>
          ) : null}
          <div>
            <p className="text-[#4a6984]/70 font-bold uppercase text-xs tracking-[0.2em]">
              Build from your resource
            </p>
            <h2 className="font-extrabold text-2xl sm:text-3xl text-[#4a6984] mt-2">
              Drop your resource — we&apos;ll anchor the lesson in it.
            </h2>
            <p className="mt-2 text-[#4a6984]/75 text-sm max-w-2xl">
              PDFs (typed or scanned), photos of textbook pages, Word docs,
              picture-book transcripts, your own notes — anything you&apos;d
              like the activity to refer to directly.
            </p>
          </div>

          {/* Drop zone */}
          <label
            htmlFor="resource-file-input"
            data-testid="resource-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`mt-7 block rounded-xl border-2 border-dashed cursor-pointer transition-all ${
              dragOver
                ? "border-[#1c2d3d] bg-[#96ded1]/15"
                : uploadFile
                  ? "border-[#96ded1] bg-[#96ded1]/10"
                  : "border-[#4a6984]/30 hover:border-[#4a6984]/55 bg-[#f4f5f7]/60"
            }`}
          >
            <div className="p-8 sm:p-10 text-center">
              {uploadFile ? (
                <div className="flex items-start justify-center gap-3 flex-wrap">
                  <FileText className="h-6 w-6 text-[#1c2d3d]" />
                  <div className="text-left">
                    <p
                      data-testid="resource-file-name"
                      className="font-extrabold text-[#1c2d3d] tracking-tight"
                    >
                      {uploadFile.name}
                    </p>
                    <p className="text-xs text-[#4a6984]/70">
                      {(uploadFile.size / 1024).toFixed(1)} KB · ready to analyse
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setUploadFile(null);
                      setFileError("");
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    data-testid="resource-file-clear"
                    className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-[#4a6984]/70 hover:text-[#4a6984]"
                  >
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center text-[#4a6984]">
                  <span className="h-12 w-12 rounded-xl bg-white border border-[#4a6984]/15 grid place-items-center mb-3">
                    <Upload className="h-5 w-5 text-[#1c2d3d]" />
                  </span>
                  <p className="font-extrabold text-[#4a6984] tracking-tight">
                    Drop a file here
                  </p>
                  <p className="text-sm text-[#4a6984]/70 mt-1">
                    or click to browse · up to {MAX_UPLOAD_MB} MB
                  </p>
                  <p className="text-[11px] text-[#4a6984]/55 mt-2 font-medium">
                    {FRIENDLY_TYPES} · scanned PDFs & photos read with OCR
                  </p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              id="resource-file-input"
              data-testid="resource-file-input"
              type="file"
              accept={ACCEPT_ATTR}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </label>

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <Label
                htmlFor="resource-paste"
                className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]"
              >
                Or paste the text
              </Label>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4a6984]/40">
                Alternative
              </span>
            </div>
            <Textarea
              id="resource-paste"
              data-testid="resource-paste"
              rows={6}
              placeholder="Paste an article, story, your teaching notes, or any text you'd like the lesson to anchor in."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md resize-none"
            />
            <div className="mt-1.5 flex items-start justify-between gap-3">
              {pasteOverLimit ? (
                <p
                  data-testid="resource-paste-error"
                  className="text-xs font-semibold text-[#c2410c] leading-relaxed"
                >
                  {TEXT_TOO_LONG_MSG}
                </p>
              ) : (
                <span />
              )}
              <p
                data-testid="resource-paste-counter"
                className={`text-xs font-semibold tabular-nums shrink-0 ${
                  pasteOverLimit ? "text-[#c2410c]" : "text-[#4a6984]/60"
                }`}
              >
                {pasteCharCount.toLocaleString()} / {MAX_PASTE_CHARS.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
            {fileError ? (
              <p
                data-testid="resource-file-error"
                className="text-xs font-semibold text-[#c2410c] leading-relaxed max-w-xl"
              >
                {fileError}
              </p>
            ) : pasteOverLimit ? (
              <p
                data-testid="resource-paste-error-bottom"
                className="text-xs font-semibold text-[#c2410c] leading-relaxed max-w-xl"
              >
                {TEXT_TOO_LONG_MSG}
              </p>
            ) : (
              <PrivacyLine>
                By uploading, you confirm you have the right to use this resource.
                Content is processed to build your lesson and immediately discarded
                — never stored.
              </PrivacyLine>
            )}
            <Button
              type="button"
              onClick={submitAnalyse}
              disabled={
                analysing ||
                (!uploadFile && !(pastedText || "").trim()) ||
                !!fileError ||
                pasteOverLimit
              }
              data-testid="resource-analyse-btn"
              className="ae-cta hover:ae-cta px-7 h-12 rounded-lg text-base inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
            >
              {analysing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Analysing…
                </>
              ) : (
                <>
                  Analyse resource <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </>
      ) : (
        // ============== STEP 2 ==============
        <>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-[#4a6984]/70 font-bold uppercase text-xs tracking-[0.2em]">
                Confirm the details
              </p>
              <h2 className="font-extrabold text-2xl sm:text-3xl text-[#4a6984] mt-2 tracking-tight">
                Looks like a great resource. Tweak anything we got wrong.
              </h2>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="resource-back-btn"
              className="text-sm font-semibold text-[#4a6984]/70 hover:text-[#4a6984] underline underline-offset-4"
            >
              Start over
            </button>
          </div>

          {/* Snippet card */}
          <div
            className="mt-6 rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7]/60 p-5"
            data-testid="resource-snippet-card"
          >
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 shrink-0 rounded-md bg-white border border-[#4a6984]/15 grid place-items-center">
                <FileText className="h-4 w-4 text-[#1c2d3d]" />
              </span>
              <div className="min-w-0">
                <p className="font-extrabold text-[#1c2d3d] tracking-tight truncate">
                  {analysis.source_label}
                </p>
                <p className="text-xs text-[#4a6984]/70 mt-0.5">
                  {analysis.char_count.toLocaleString()} characters extracted
                  {analysis.suggested?.title_guess
                    ? ` · ${analysis.suggested.title_guess}`
                    : ""}
                </p>
                <p className="mt-3 text-sm text-[#4a6984] leading-relaxed italic">
                  “{analysis.snippet}”
                </p>
              </div>
            </div>
          </div>

          {/* Smart suggestion — one-tap "Atomic Edu suggests…" banner.
              Shown only when the classifier returned enough fields to
              build immediately. Below it the full confirm form still
              renders so teachers can tweak before building. */}
          {smartSuggestion && (
            <div
              data-testid="resource-smart-suggestion"
              className="mt-6 rounded-xl border-2 border-[#96ded1] bg-[#96ded1]/15 p-5 sm:p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#1c2d3d] flex items-center gap-1.5">
                    <Wand2 className="h-3.5 w-3.5" />
                    Atomic Edu suggests
                  </p>
                  <p
                    data-testid="resource-smart-headline"
                    className="mt-1 font-black text-lg sm:text-xl text-[#1c2d3d] tracking-tight leading-snug"
                  >
                    {smartSuggestion.headline}
                  </p>
                  {smartSuggestion.reason && (
                    <p className="mt-1 text-xs font-bold text-[#4a6984]/85">
                      {smartSuggestion.reason}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={buildFromSuggestion}
                  disabled={loading || !analysis?.extracted_text}
                  data-testid="resource-smart-build"
                  className="ae-cta hover:ae-cta h-11 px-5 rounded-md font-extrabold inline-flex items-center gap-2 shrink-0 disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  Build it now
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-3 text-xs text-[#4a6984]/65 italic">
                Or pick a different format below.
              </p>
            </div>
          )}

          {/* Output-type tag buttons */}
          <div className="mt-7">
            <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
              Output type
            </Label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="resource-output-type">
              {OUTPUT_OPTIONS.map((opt) => {
                const active = confirm.output_type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`resource-output-${opt.value}`}
                    onClick={() => setConfirm((c) => ({ ...c, output_type: opt.value }))}
                    className={`text-left rounded-lg border-2 px-4 py-3 transition-all ${
                      active
                        ? "border-[#1c2d3d] bg-[#1c2d3d] text-white"
                        : "border-[#4a6984]/15 bg-white text-[#4a6984] hover:border-[#4a6984]/35"
                    }`}
                  >
                    <p className="font-extrabold text-sm tracking-tight">
                      {opt.label}
                    </p>
                    <p
                      className={`text-xs font-medium mt-0.5 ${
                        active ? "text-white/75" : "text-[#4a6984]/70"
                      }`}
                    >
                      {opt.hint}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grade / Subject / Skill */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-2">
              <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                Grade
              </Label>
              <Select
                value={confirm.grade}
                onValueChange={(v) => setConfirm((c) => ({ ...c, grade: v }))}
              >
                <SelectTrigger data-testid="resource-grade" className={fieldClass}>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                  {gradeOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                Subject
              </Label>
              <Select
                value={confirm.subject}
                onValueChange={(v) => setConfirm((c) => ({ ...c, subject: v }))}
              >
                <SelectTrigger data-testid="resource-subject" className={fieldClass}>
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                  {subjectOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                Skill focus
              </Label>
              <Select
                value={confirm.skill_focus}
                onValueChange={(v) => setConfirm((c) => ({ ...c, skill_focus: v }))}
              >
                <SelectTrigger data-testid="resource-skill" className={fieldClass}>
                  <SelectValue placeholder="Select skill focus" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                  {SKILL_FOCUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {confirm.grade && confirm.subject && strandOptions.length > 0 && (
              <div className="space-y-2">
                <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                  Strand{" "}
                  <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">
                    (optional)
                  </span>
                </Label>
                <Select
                  value={confirm.strand}
                  onValueChange={(v) => setConfirm((c) => ({ ...c, strand: v }))}
                >
                  <SelectTrigger data-testid="resource-strand" className={fieldClass}>
                    <SelectValue placeholder="Any strand" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                    {strandOptions.map((s) => (
                      <SelectItem key={s.code} value={s.code}>
                        {s.code} — {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {confirm.strand && hasCuratedSpecifics && (
              <div className="space-y-2">
                <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                  Specific expectation{" "}
                  <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">
                    (optional)
                  </span>
                </Label>
                <Select
                  value={confirm.expectation || ""}
                  onValueChange={(v) =>
                    setConfirm((c) => ({ ...c, expectation: v === "__clear__" ? "" : v }))
                  }
                >
                  <SelectTrigger data-testid="resource-expectation" className={fieldClass}>
                    <SelectValue placeholder="Any expectation in this strand" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984] max-h-80">
                    {confirm.expectation ? (
                      <SelectItem
                        value="__clear__"
                        data-testid="resource-expectation-option-clear"
                        className="text-[#4a6984]/70 italic font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
                      >
                        Any expectation in this strand
                      </SelectItem>
                    ) : null}
                    {specificOptions.map((row) => (
                      <SelectItem
                        key={row.code}
                        value={row.code}
                        data-testid={`resource-expectation-option-${row.code.toLowerCase()}`}
                        className="text-[#4a6984] font-medium focus:bg-[#f4f5f7] focus:text-[#4a6984]"
                      >
                        <span className="flex flex-col gap-0.5 py-0.5 text-left">
                          <span className="font-extrabold text-[#1c2d3d] text-[13px] tracking-tight">
                            {row.code}
                            {row.topic ? (
                              <span className="ml-2 text-[#4a6984]/70 font-semibold">
                                · {row.topic}
                              </span>
                            ) : null}
                          </span>
                          {row.description ? (
                            <span className="text-[11px] text-[#4a6984]/80 leading-snug max-w-[28rem] whitespace-normal">
                              {row.description}
                            </span>
                          ) : null}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {confirm.strand && !hasCuratedSpecifics && (
              <div className="space-y-2">
                <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                  Specific expectation{" "}
                  <span className="text-[#4a6984]/40 font-medium normal-case tracking-normal text-[10px]">
                    (optional)
                  </span>
                </Label>
                <Input
                  data-testid="resource-expectation"
                  value={confirm.expectation}
                  onChange={(e) =>
                    setConfirm((c) => ({ ...c, expectation: e.target.value }))
                  }
                  placeholder={`e.g. ${confirm.strand}1.4`}
                  className={fieldClass}
                />
              </div>
            )}
            {isUnitChoice && (
              <div className="space-y-2">
                <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                  # of lessons
                </Label>
                <Select
                  value={confirm.num_lessons}
                  onValueChange={(v) => setConfirm((c) => ({ ...c, num_lessons: v }))}
                >
                  <SelectTrigger data-testid="resource-num-lessons" className={fieldClass}>
                    <SelectValue placeholder="Pick" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#4a6984]/15 text-[#4a6984]">
                    {LESSON_COUNT_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt} lessons
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2">
            <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
              Anything specific? (optional)
            </Label>
            <Textarea
              data-testid="resource-notes"
              rows={3}
              placeholder="e.g. Tie this to our novel study on Wonder. My class has two IEPs."
              value={confirm.notes}
              onChange={(e) => setConfirm((c) => ({ ...c, notes: e.target.value }))}
              className="bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md resize-none"
            />
          </div>

          <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
            <PrivacyLine>
              Resource will be discarded after your lesson is generated.
            </PrivacyLine>
            <Button
              type="button"
              onClick={submitBuild}
              disabled={loading}
              data-testid="resource-build-btn"
              className="ae-cta hover:ae-cta px-7 h-12 rounded-lg text-base inline-flex items-center gap-2 shadow-sm whitespace-nowrap"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" /> Build my lesson{" "}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ResourceBuilder;
